import { isValid, startOfDay } from 'date-fns'
import { ArticleFromApi } from 'products/types'
import { DateFormats, formatOrInvalid, parseISODate } from 'utils/dates'
import { PRODUCTS } from 'capacity/copy'

export type OnSaleFactorKey = 'enabled' | 'showDates' | 'validTo' | 'category'

export interface OnSaleFactor {
    key: OnSaleFactorKey
    met: boolean
    date: { kind: 'showFrom' | 'showTo' | 'validTo'; date: Date } | null
}

export interface OnSaleFactorLine {
    key: OnSaleFactorKey
    label: string
    detail: string | null
    met: boolean
}

const calendarDate = (value: string | null | undefined): Date | null => {
    if (!value) {
        return null
    }
    const parsed = parseISODate(value)
    return isValid(parsed) ? startOfDay(parsed) : null
}

const showDatesFactor = (showFrom: Date | null, showTo: Date | null, day: Date): OnSaleFactor => {
    const notStarted = !!showFrom && showFrom.getTime() > day.getTime()
    const ended = !!showTo && showTo.getTime() < day.getTime()
    let date: OnSaleFactor['date'] = null
    if (notStarted && showFrom) {
        date = { kind: 'showFrom', date: showFrom }
    } else if (showTo) {
        date = { kind: 'showTo', date: showTo }
    } else if (showFrom) {
        date = { kind: 'showFrom', date: showFrom }
    }
    return { key: 'showDates', met: !notStarted && !ended, date }
}

export function onSaleFactors(article: ArticleFromApi, today: Date): OnSaleFactor[] {
    const day = startOfDay(today)
    const validTo = calendarDate(article.validTo)
    return [
        { key: 'enabled', met: !!article.enabled, date: null },
        showDatesFactor(calendarDate(article.showFrom), calendarDate(article.showTo), day),
        {
            key: 'validTo',
            met: !validTo || validTo.getTime() >= day.getTime(),
            date: validTo ? { kind: 'validTo', date: validTo } : null,
        },
        { key: 'category', met: article.categories.length > 0, date: null },
    ]
}

export const isOnSaleNow = (article: ArticleFromApi, today: Date): boolean =>
    onSaleFactors(article, today).every((factor) => factor.met)

const factorDetail = (date: NonNullable<OnSaleFactor['date']>): string => {
    const text = formatOrInvalid(date.date, DateFormats.LONG_DATE)
    switch (date.kind) {
        case 'showFrom':
            return PRODUCTS.onSale.shownFrom(text)
        case 'showTo':
            return PRODUCTS.onSale.shownUntil(text)
        default:
            return PRODUCTS.onSale.validUntil(text)
    }
}

export const onSaleFactorLines = (article: ArticleFromApi, today: Date): OnSaleFactorLine[] =>
    onSaleFactors(article, today).map((factor) => ({
        key: factor.key,
        label: PRODUCTS.onSale.factors[factor.key],
        detail: factor.date ? factorDetail(factor.date) : null,
        met: factor.met,
    }))
